import { BodyText, Button, Flex } from '@utilitywarehouse/hearth-react';
import { useEffect } from 'react';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import { ProductType } from '@generated/gql/graphql';

interface OutdatedBundleModalProps {
  isOpen: boolean;
  removedProducts: Nullable<ProductType[]>;
  onContinue: () => void;
}

interface OutdatedBundleModalUIProps {
  dialogId: DialogID;
  removedProducts: Nullable<ProductType[]>;
  onContinue: () => void;
}

const formatProductLabel = (product: ProductType) => {
  switch (product) {
    case ProductType.Energy:
      return 'Energy';
    case ProductType.Broadband:
      return 'Broadband';
    case ProductType.Mobile:
      return 'Mobile';
    case ProductType.Insurance:
      return 'Insurance';
    default:
      return product;
  }
};

const OutdatedBundleModalUI = ({
  dialogId,
  removedProducts,
  onContinue,
}: OutdatedBundleModalUIProps) => {
  const hasRemovedProducts = removedProducts && removedProducts.length > 0;

  return (
    <Dialog
      id={dialogId}
      heading="Your bundle has updated"
      hideCloseButton
      preventDismiss
      data-testid="outdated-bundle-modal"
    >
      <Flex direction="column" gap="300">
        {hasRemovedProducts && (
          <>
            <BodyText>
              We're sorry, the following product(s) are no longer available and
              have been removed from your bundle:
            </BodyText>
            <ul data-testid="outdated-bundle-modal-removed-products">
              {removedProducts.map((product) => (
                <li key={product}>
                  <BodyText>{formatProductLabel(product)}</BodyText>
                </li>
              ))}
            </ul>
          </>
        )}
        <BodyText data-testid="outdated-bundle-modal-description">
          {!hasRemovedProducts && (
            <>
              There have been some changes to our services since you first got
              your quote from us.{' '}
            </>
          )}
          Please check your updated bundle to make sure you're happy before you
          confirm your order.
        </BodyText>
      </Flex>
      <DialogActions>
        <Button
          data-testid="outdated-bundle-modal-continue"
          onClick={onContinue}
          variant="solid"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OutdatedBundleModal = ({
  isOpen,
  removedProducts,
  onContinue,
}: OutdatedBundleModalProps) => {
  const dialog = useDialog(DialogID.OUTDATED_BUNDLE);

  useEffect(() => {
    dialog.setOpen(isOpen);
  }, [dialog, isOpen]);

  return (
    <OutdatedBundleModalUI
      dialogId={dialog.id}
      removedProducts={removedProducts}
      onContinue={onContinue}
    />
  );
};

export type { OutdatedBundleModalProps, OutdatedBundleModalUIProps };

export { OutdatedBundleModal, OutdatedBundleModalUI };
